(() => {
  if (!('customElements' in window) || customElements.get('ct-reviews-hub')) return;

  const STAR_PATH = 'M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z';
  const SVG_NS = 'http://www.w3.org/2000/svg';

  class CtReviewsHub extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.grid = this.querySelector('[data-ct-reviews-grid]');
      this.items = Array.from(this.querySelectorAll('[data-ct-review-item]'));
      this.initialRenderedCount = this.items.length;
      this.actions = this.querySelector('[data-ct-reviews-actions]');
      this.button = this.querySelector('[data-ct-reviews-load-more]');
      this.liveRegion = this.querySelector('[data-ct-reviews-live]');
      this.initialCount = this.positiveInteger(this.dataset.initialCount, 3);
      this.batchSize = this.positiveInteger(this.dataset.batchSize, 3);
      this.visibleCount = Math.min(this.initialCount, this.items.length);
      this.sourceTotal = this.positiveInteger(this.dataset.sourceTotal, this.items.length);
      this.progressiveUrl = this.dataset.progressiveUrl || '';
      this.expectedProgressiveKey = this.dataset.progressiveKey || '';
      this.loadMoreLabel = this.dataset.loadMoreLabel || 'Ver más reseñas';
      this.loadingLabel = this.dataset.loadingLabel || 'Cargando reseñas…';
      this.loadErrorLabel = this.dataset.loadErrorLabel || 'Reintentar carga';
      this.allLoadedLabel = this.dataset.allLoadedLabel || 'Todas las opiniones están visibles';
      this.progressiveReviews = [];
      this.progressiveCursor = 0;
      this.progressiveLoaded = false;
      this.loading = false;
      this.initialized = true;

      if (!this.button || !this.actions || !this.grid || this.sourceTotal <= this.visibleCount) return;

      this.handleLoadMore = this.handleLoadMore.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.classList.add('ct-reviews-hub--enhanced');
      this.updateVisibility(false);
      this.actions.hidden = false;
      this.button.addEventListener('click', this.handleLoadMore);

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.handleBlockSelect);
      }
    }

    disconnectedCallback() {
      if (this.button && this.handleLoadMore) this.button.removeEventListener('click', this.handleLoadMore);
      if (this.handleBlockSelect) document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      if (this.abortController) this.abortController.abort();
      this.initialized = false;
    }

    positiveInteger(value, fallback) {
      const parsed = Number.parseInt(value, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
    }

    async handleLoadMore() {
      if (this.loading) return;

      if (this.visibleCount < this.items.length) {
        this.visibleCount = Math.min(this.visibleCount + this.batchSize, this.items.length);
        this.updateVisibility(true);
        return;
      }

      if (!this.progressiveLoaded && this.progressiveUrl) {
        const loaded = await this.loadProgressiveReviews();
        if (!loaded) return;
      }

      this.appendProgressiveBatch();
    }

    async loadProgressiveReviews() {
      this.loading = true;
      this.button.disabled = true;
      this.button.textContent = this.loadingLabel;
      this.abortController = new AbortController();

      try {
        const response = await fetch(this.progressiveUrl, {
          credentials: 'same-origin',
          signal: this.abortController.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        const validatedReviews = this.validateProgressivePayload(payload);

        this.progressiveReviews = validatedReviews;
        this.progressiveLoaded = true;
        return true;
      } catch (error) {
        if (error.name === 'AbortError') return false;

        this.progressiveReviews = [];
        this.progressiveCursor = 0;
        this.progressiveLoaded = false;
        if (this.isConnected) {
          this.button.disabled = false;
          this.button.textContent = this.loadErrorLabel;
          if (this.liveRegion) this.liveRegion.textContent = 'No se pudieron cargar más opiniones. Podés volver a intentar.';
        }
        return false;
      } finally {
        this.loading = false;
        this.abortController = null;
      }
    }

    validateProgressivePayload(payload) {
      if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
        throw new Error('Formato de reseñas inválido');
      }

      const { k: datasetKey, total, initial, reviews } = payload;
      if (
        typeof datasetKey !== 'string'
        || this.expectedProgressiveKey.length === 0
        || datasetKey !== this.expectedProgressiveKey
      ) {
        throw new Error('El archivo histórico no corresponde al producto esperado');
      }
      if (!Number.isInteger(total) || total < 1) throw new Error('Total histórico inválido');
      if (!Number.isInteger(initial) || initial < 0 || initial > total) {
        throw new Error('Cantidad inicial inválida');
      }
      if (!Array.isArray(reviews)) throw new Error('Listado de reseñas inválido');

      const initialDomIsUntouched = this.items.length === this.initialRenderedCount
        && this.visibleCount === this.initialRenderedCount
        && this.progressiveCursor === 0;
      if (!initialDomIsUntouched || initial !== this.initialRenderedCount) {
        throw new Error('Las reseñas iniciales no coinciden con el archivo histórico');
      }
      if (total !== this.sourceTotal || reviews.length !== total - initial) {
        throw new Error('Los totales del archivo histórico no coinciden');
      }
      const reviewIds = new Set();
      for (const review of reviews) {
        if (!this.isValidProgressiveReview(review)) {
          throw new Error('El archivo histórico contiene una reseña inválida');
        }

        const reviewId = review.i.trim();
        if (reviewIds.has(reviewId)) {
          throw new Error('El archivo histórico contiene identificadores duplicados');
        }
        reviewIds.add(reviewId);
      }

      return reviews;
    }

    isValidProgressiveReview(review) {
      if (!review || typeof review !== 'object' || Array.isArray(review)) return false;

      const publicStringFields = ['n', 'c', 'd', 't', 'x'];
      const hasPublicStrings = publicStringFields.every((field) => typeof review[field] === 'string');
      const hasRequiredContent = hasPublicStrings
        && ['n', 't', 'x'].every((field) => review[field].trim().length > 0);
      const hasValidId = typeof review.i === 'string' && review.i.trim().length > 0;
      const hasValidProduct = review.p === undefined
        || typeof review.p === 'string';
      const hasStoreProduct = this.expectedProgressiveKey !== 'store'
        || (typeof review.p === 'string' && review.p.trim().length > 0);
      const hasValidStars = Number.isInteger(review.s) && review.s >= 1 && review.s <= 5;

      return hasPublicStrings
        && hasRequiredContent
        && hasValidId
        && hasValidProduct
        && hasStoreProduct
        && hasValidStars;
    }

    appendProgressiveBatch() {
      const remainingSlots = Math.max(this.sourceTotal - this.items.length, 0);
      const nextBatchSize = Math.min(this.batchSize, remainingSlots);
      const batch = this.progressiveReviews.slice(
        this.progressiveCursor,
        this.progressiveCursor + nextBatchSize,
      );
      if (batch.length === 0) {
        this.updateVisibility(true);
        return;
      }

      const fragment = document.createDocumentFragment();
      batch.forEach((review) => fragment.append(this.createReviewItem(review)));
      this.grid.append(fragment);
      this.progressiveCursor += batch.length;
      this.items = Array.from(this.querySelectorAll('[data-ct-review-item]'));
      this.visibleCount = this.items.length;
      this.updateVisibility(true);
    }

    createReviewItem(review) {
      const item = this.element('div', 'ct-reviews-hub__item');
      item.dataset.ctReviewItem = '';
      item.dataset.ctReviewId = review.i.trim();
      item.setAttribute('role', 'presentation');

      const article = this.element('article', 'ct-review-card');
      article.dataset.ctReviewCard = '';
      article.setAttribute('role', 'listitem');
      article.setAttribute('aria-label', `Opinión de ${review.n}`);

      const ratingRow = this.element('div', 'ct-review-card__rating-row');
      const stars = this.element('div', 'ct-review-card__stars');
      const starCount = Math.max(1, Math.min(5, Number(review.s) || 5));
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', `${starCount} de 5 estrellas`);
      for (let index = 1; index <= 5; index += 1) stars.append(this.createStar(index <= starCount));
      ratingRow.append(stars);
      if (review.d) ratingRow.append(this.element('span', 'ct-review-card__date', review.d));
      article.append(ratingRow);

      const titleText = review.p ? `${review.p}: ${review.t}` : review.t;
      article.append(this.element('h3', 'ct-review-card__title', titleText));
      article.append(this.element('p', 'ct-review-card__comment', review.x));

      const footer = this.element('footer', 'ct-review-card__author');
      const avatar = this.element('span', 'ct-review-card__avatar-fallback', review.n.trim().slice(0, 1).toLocaleUpperCase('es-PY') || '★');
      avatar.setAttribute('aria-hidden', 'true');
      footer.append(avatar);

      const authorInfo = this.element('div', 'ct-review-card__author-info');
      authorInfo.append(this.element('p', 'ct-review-card__name', review.n));
      if (review.c) {
        const meta = this.element('div', 'ct-review-card__meta');
        meta.append(this.element('span', '', review.c));
        authorInfo.append(meta);
      }
      footer.append(authorInfo);
      article.append(footer);
      item.append(article);
      return item;
    }

    createStar(filled) {
      const svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
      const path = document.createElementNS(SVG_NS, 'path');
      path.setAttribute('d', STAR_PATH);
      if (filled) {
        path.setAttribute('fill', 'currentColor');
      } else {
        path.setAttribute('fill', 'none');
        path.setAttribute('stroke', 'currentColor');
        path.setAttribute('stroke-width', '1.5');
      }
      svg.append(path);
      return svg;
    }

    element(tagName, className, text) {
      const element = document.createElement(tagName);
      if (className) element.className = className;
      if (text !== undefined) element.textContent = text;
      return element;
    }

    handleBlockSelect(event) {
      const selectedBlockId = event.detail && event.detail.blockId ? String(event.detail.blockId) : '';
      let selectedItem = selectedBlockId
        ? this.items.find((item) => item.dataset.blockId === selectedBlockId)
        : null;
      if (!selectedItem && event.target instanceof Element && this.contains(event.target)) {
        selectedItem = event.target.closest('[data-ct-review-item]');
      }
      const selectedIndex = this.items.indexOf(selectedItem);
      if (selectedIndex < 0 || selectedIndex < this.visibleCount) return;
      this.visibleCount = selectedIndex + 1;
      this.updateVisibility(false);
    }

    updateVisibility(announce) {
      this.items.forEach((item, index) => { item.hidden = index >= this.visibleCount; });

      const remaining = Math.max(this.sourceTotal - this.visibleCount, 0);
      const nextBatch = Math.min(this.batchSize, remaining);
      const hasExpanded = this.visibleCount > this.initialCount;
      this.button.setAttribute('aria-expanded', String(hasExpanded));

      if (remaining === 0 || (this.progressiveLoaded && this.progressiveCursor >= this.progressiveReviews.length && this.visibleCount >= this.items.length)) {
        this.button.textContent = this.allLoadedLabel;
        this.button.disabled = true;
        this.button.setAttribute('aria-expanded', 'true');
      } else {
        this.button.textContent = `${this.loadMoreLabel} (${nextBatch})`;
        this.button.disabled = false;
      }

      if (announce && this.liveRegion) {
        this.liveRegion.textContent = `Se muestran ${this.visibleCount} de ${this.sourceTotal} opiniones escritas.`;
      }
    }
  }

  customElements.define('ct-reviews-hub', CtReviewsHub);
})();
